/**
 * One conversation (channel, DM, or a thread when `threadRootId` is set): timeline with day separators and
 * the "new" divider, older pages, offline-queued sends, read marker, typing (throttled out, labelled in),
 * agent status lines and the external-organization banner.
 */
import type { AvailableCommand, BlockActionRequest, Capabilities, ChannelKind, EphemeralMessage, FileRef, KitBlock, Member, Message, ReminderRepeat, UserStatus, Workflow } from "@bundle/contracts";
import { activeStatus } from "../logic/profile.ts";
import { channelWorkflows } from "../logic/workflows.ts";
import { slashInvocation } from "../logic/integrations.ts";
import { type KitButtonNode, type KitOverflowNode, type KitValueNode, type KitValues, kitState, kitStateValue, renderKit } from "../logic/blockKit.ts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { attachmentsProgress, attachProblem, dedupeFiles, fileIcon, fileKind, uploadLabel, uploadProgress, type FileKind, type UploadPhase } from "../logic/files.ts";
import { capabilitiesFor, externalOrganizations, isExternal } from "../logic/access.ts";
import { agentStatusLines, type AgentStatusLine } from "../logic/agentStatus.ts";
import {
  applyCompletion,
  autocompleteMenu,
  channelItems,
  commandHint,
  commandItems,
  completionToken,
  emojiItems,
  findTrigger,
  insertTrigger,
  mentionItems,
  moveActive,
  type AutocompleteItem,
  type BroadcastRange,
  type AutocompleteKind,
  type AutocompleteMenuState,
  type CommandHint,
  type EmojiEntry,
  type WorkflowShortcut,
} from "../logic/autocomplete.ts";
import { introPrompt } from "../logic/channels.ts";
import { broadcastWarning, draftBroadcast, type BroadcastWarning } from "../logic/broadcast.ts";
import { memberPaused } from "../logic/dnd.ts";
import { agentActivities, agentState } from "../logic/live.ts";
import { canDeleteMessage, canEditMessage, editOutcome, flattenPages, messagePreview, reactionToggle, type EditPlace, type MessagePage } from "../logic/messages.ts";
import { pendingFor, type PendingEntry, type SendInput } from "../logic/outbox.ts";
import { agentPresence, channelTitle, type AvatarPresence } from "../logic/sidebar.ts";
import { REVEAL_MAX_PAGES, buildTimeline, newMessageCount, revealPageSize, threadLabels, withThreadBroadcasts, type TimelineItem, type TimelineOptions } from "../logic/timeline.ts";
import { activeTypers, shouldSendTyping, typingLabel } from "../logic/typing.ts";
import { memberName, type WorkspaceCache } from "../logic/workspace.ts";
import { blockedDmPeer, canBlock, canReport, isCollapsed } from "../logic/moderation.ts";
import { messageUrl } from "../logic/shell.ts";
import { isRemindCommand, parseRemind } from "../logic/later.ts";
import { pickDraft } from "../logic/drafts.ts";
import { sendAtProblem } from "../logic/scheduled.ts";
import type { LivePagedQuery, LiveQuery, Observable, PagedState, PickedFile, QueryState, UploadEntry } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** A file attached to the draft (composer chip). */
export type AttachmentView = {
  id: string;
  name: string;
  mime: string;
  size: number;
  kind: FileKind;
  /** Icon key (packages/tokens/icons.json). */
  icon: string;
  /** Local preview for images/videos (`local-upload:<id>`). */
  previewUrl?: string;
  phase: UploadPhase;
  /** 0…1 */
  progress: number;
  label: MessageDescriptor;
};

/** The other person in a 1:1 DM (header avatar + presence). */
export type DmPeer = { userId: string; name: string; avatarUrl?: string; presence?: AvatarPresence; isAgent: boolean; /** Do not disturb is on (a moon). */ dnd?: boolean; /** Their custom status (DM header: emoji after the name, text and "until" under it; logic/profile statusDisplay). */ status?: UserStatus };

export type ChannelViewState = {
  channelId: string;
  threadRootId?: string;
  status: "loading" | "ready" | "error";
  title: string;
  kind?: ChannelKind;
  topic?: string;
  memberCount: number;
  isMember: boolean;
  /** Includes people from other organizations. */
  external: boolean;
  peer?: DmPeer;
  externalBanner?: MessageDescriptor;
  items: TimelineItem[];
  /** Thread root (thread mode). */
  root?: Message;
  hasOlder: boolean;
  loadingOlder: boolean;
  /** Messages by others after the "new" divider. */
  newCount: number;
  typing?: MessageDescriptor;
  agentLines: AgentStatusLine[];
  draft: string;
  composerPlaceholder: MessageDescriptor;
  caps: Capabilities;
  /** Offline: sends are queued. */
  offline: boolean;
  /** Files attached to the draft, in order (uploading in the background, offline included). */
  attachments: AttachmentView[];
  /** Why the last picked files weren't all attached (too many, too big, already attached). */
  attachNotice?: MessageDescriptor;
  /** Upload progress (0…1) of queued messages that wait for their files, by clientId. */
  sendProgress: Record<string, number>;
  /** Full-pane drop overlay ("Drop files to share in #design"). */
  dropLabel: MessageDescriptor;
  /** The draft has text or files and I may post here. */
  canSend: boolean;
  /** Composer autocomplete (@ people and agents, # channels, : emoji, / commands); absent = closed. */
  autocomplete?: AutocompleteMenuState;
  /** Puts the composer caret at [start, end] and focuses it (after a completion or the @ button); `seq` changes per request. */
  composerSelection?: { start: number; end: number; seq: number };
  /** Arguments of the built-in command the draft starts with ("/remind " → "[@someone or #channel] [what] [when]"). */
  commandHint?: CommandHint;
  /** Composer autocomplete is on (WorkspaceDeps.autocomplete); off = no menu and the @ button just types "@". */
  composerAutocomplete: boolean;
  /**
   * Thread composer: the "Also send to #design" / "Also send as direct message" checkbox (remembered with the thread's
   * draft, cleared by a send). Absent = not offered: the main composer, or I can't post in the channel (not a member,
   * or it is archived).
   */
  alsoSend?: { checked: boolean; label: MessageDescriptor };
  /** Thread: the marker on replies that were also sent to the channel ("Also sent to #design"). */
  alsoSentLabel?: MessageDescriptor;
  /** Thread: where it is, under its title ("in #design", "with Bo Chen"). */
  threadIn?: MessageDescriptor;
  /**
   * The message of mine being edited in place: its row shows an edit composer with `draft` (the message's token text,
   * so mentions show as chips) instead of the body. `canSave` = the draft isn't empty. Absent = not editing.
   */
  editing?: EditingState;
  /** "Delete message?" confirmation for a message (askDelete); absent = closed. */
  deleting?: DeleteConfirmState;
  /**
   * Slack's "Are you sure?" before a draft's @channel / @everyone / @here notifies more than 6 people (logic/broadcast):
   * how many and in how many time zones; confirmBroadcast sends, cancelBroadcast keeps the draft. Absent = closed.
   */
  broadcastConfirm?: BroadcastWarning;
  /** The Pinned list (header button → openPins): the conversation's pinned messages; absent = closed. */
  pins?: PinsState;
  /**
   * Threads I follow, as far as I know (root id → following; Slack "Get notified about new replies"): the thread
   * header's toggle (a thread loads its root's on open) and the message menu (loadFollow when it opens). Replying
   * follows. Absent = the API port can't follow threads (no toggle shown).
   */
  follows?: Record<string, boolean>;
  /** The line under the conversation intro ("Send a message to get the conversation going."); absent when archived. */
  introPrompt?: MessageDescriptor;
  /**
   * The answer to a command the composer ran instead of posting (`/remind me …`, logic/later): "reminded" with the
   * reminder's text and time (the UI formats it), or an error to show. Absent = nothing to say.
   */
  commandNotice?: CommandNotice;
  /** This channel's link workflows (Workflow Builder), by name: its workflows menu and the `/` menu. */
  workflows?: WorkflowShortcut[];
  /** A workflow started from here is starting. */
  runningWorkflow?: string;
  /**
   * Messages only I see here ("Only visible to you": slash command and button answers), oldest first. Each shows
   * after the newest message created before it (`createdAt`). Dismissible; gone on reload.
   */
  ephemerals: EphemeralMessage[];
  /** An app command is running (its answer hasn't come back yet). */
  runningCommand?: string;
  /**
   * Messages (root included in a thread) from people I blocked that show as one muted row, "Message from someone
   * you blocked · Show" (logic/moderation `isCollapsed`), until `showBlocked`. Absent = none.
   */
  collapsedIds?: string[];
  /** A 1:1 DM with someone I blocked: the composer is replaced by "You blocked @name · Unblock". Absent otherwise. */
  blockedPeer?: { userId: string; name: string };
};

export type CommandNotice =
  /** `who`: "@Ana" / "#design" when the reminder is for someone else or a conversation; `repeats`: recurring. */
  | { kind: "reminded"; text: string; at: number; who?: string; repeats?: boolean }
  /** The draft was scheduled (Slack "Schedule message") for `at`. */
  | { kind: "scheduled"; at: number }
  | { kind: "error"; message: MessageDescriptor };

/** Who else a `/remind` is for, and whether it repeats (contracts SaveLaterRequest). */
export type ReminderOptions = { repeat?: ReminderRepeat; forUserId?: string; forChannelId?: string };

/** What the composer's "Schedule message" hands to the workspace's ScheduledViewModel. */
export type ScheduleRequest = { channelId: string; text: string; sendAt: number; fileIds?: string[]; threadRootId?: string; alsoSendToChannel?: boolean };

/** The Pinned list: `loading` until the server answers (refreshed when I pin or unpin while it is open). */
export type PinsState = { status: "loading" | "ready" | "error"; messages: Message[] };

export type EditingState = {
  messageId: string;
  draft: string;
  canSave: boolean;
  /** The editor's autocomplete (@ people, # channels, : emoji — no / commands); absent = closed. */
  autocomplete?: AutocompleteMenuState;
  /** Puts the editor's caret at [start, end] and focuses it (opening the editor, after a completion); `seq` changes per request. */
  selection?: { start: number; end: number; seq: number };
};

/**
 * "Delete message?" (askDelete → confirmDelete / cancelDelete): what the confirmation shows about the message.
 */
export type DeleteConfirmState = {
  messageId: string;
  /** Who wrote it (an app's name for app messages). */
  authorName: string;
  /** The message as one line (core messagePreview); "" when it has no text, sticker or GIF title. */
  preview: string;
  /** Attached files, deleted with it. */
  fileCount: number;
  /** Someone else's message (an owner or admin deleting it). */
  others: boolean;
  /** A thread root with replies: "This message was deleted." stays in its place and the replies are kept. */
  hasReplies: boolean;
};

/** Which field an autocomplete menu belongs to. */
type AutocompleteSlot = "composer" | "edit";
/** Escape closed the menu of the token typed at `start`: it stays closed until that token ends. */
type Dismissed = { kind: AutocompleteKind; start: number };

/** Longest message preview in the delete confirmation, in characters. */
const DELETE_PREVIEW_MAX = 140;

/** Composer keys the autocomplete menu may take. */
export type ComposerKey = "up" | "down" | "enter" | "tab" | "escape";

const RECENT_EMOJI_KEY = "bundle.recentEmoji";
const RECENT_EMOJI_MAX = 24;

const NO_DIVIDER = Number.MAX_SAFE_INTEGER;
const DRAFT_PREFIX = "bundle.draft.";
const DRAFT_FILES_PREFIX = "bundle.draftFiles.";
const DRAFT_ALSO_SEND_PREFIX = "bundle.draftAlsoSend.";
const NO_PROGRESS: Record<string, number> = {};
const NO_ATTACHMENTS: AttachmentView[] = [];

export class ChannelViewModel extends ViewModel<ChannelViewState> {
  private readonly ws: WorkspaceViewModel;
  private readonly paged: LivePagedQuery<MessagePage> | undefined = undefined;
  private readonly single: LiveQuery<MessagePage> | undefined = undefined;
  private readonly outbox: Observable<PendingEntry[]>;
  private dividerSeq: number | undefined;
  /** Blocked people's messages I chose to Show (this view only). */
  private shownBlocked: string[] = [];
  private lastTypingSentAt: number | undefined;
  private visible = false;
  private markedSeq = 0;
  /** "Mark unread" was used: don't mark the conversation read again until I leave it (like Slack). */
  private holdRead = false;
  /** The composer changed since this conversation opened (the server's draft no longer replaces it). */
  private typedSinceOpen = false;
  private readonly draftKey: string;
  private readonly draftFilesKey: string;
  private readonly draftAlsoSendKey: string;
  /** Thread composer's "also send to the channel" checkbox (persisted with the draft). */
  private alsoSendChecked: boolean;
  private reminderSink: ((text: string, at: number, opts: ReminderOptions) => Promise<unknown>) | undefined;
  private scheduleSink: ((req: ScheduleRequest) => Promise<unknown>) | undefined;
  private emojiSink: ((emoji: string) => string | void) | undefined;
  private readonly uploads: Observable<UploadEntry[]>;
  /** Upload ids attached to the draft (persisted with the draft). */
  private attachmentIds: string[];
  /** Composer caret (UTF-16 offset in the draft), as last reported by the field or estimated from the edit. */
  private caret = 0;
  private selectionCollapsed = true;
  private dismissed: Dismissed | undefined;
  /** The workflows the menu was last built with (WorkspaceViewModel.workflows). */
  private seenWorkflows: Workflow[] | undefined;
  /** The app commands the menu was last built with (WorkspaceViewModel.appCommands). */
  private seenCommands: readonly AvailableCommand[] | undefined;
  /** The in-place editor's caret, selection and dismissed menu (like the composer's above). */
  private editCaret = 0;
  private editCollapsed = true;
  private editDismissed: Dismissed | undefined;
  private emoji: EmojiEntry[] | undefined;
  private emojiLoading = false;
  /** A broadcast draft is being counted (askBroadcast): a second Enter doesn't send it twice. */
  private sendingBroadcast = false;

  constructor(ws: WorkspaceViewModel, channelId: string, threadRootId?: string) {
    const scope = `${ws.wsId}.${channelId}${threadRootId === undefined ? "" : `.${threadRootId}`}`;
    const draftKey = `${DRAFT_PREFIX}${scope}`;
    const initial: ChannelViewState = {
      channelId,
      status: "loading",
      title: "",
      memberCount: 0,
      isMember: false,
      external: false,
      items: [],
      hasOlder: false,
      loadingOlder: false,
      newCount: 0,
      agentLines: [],
      draft: ws.deps.storage.get(draftKey) ?? "",
      ephemerals: [],
      composerPlaceholder: msg("composer.placeholderThread"),
      caps: {
        dm: "anyone", seeDirectory: "all", createChannels: false, invite: false, search: false,
        downloadFiles: false, joinHuddles: false, mentionBroadcast: false, seeEmails: false,
      },
      offline: false,
      attachments: NO_ATTACHMENTS,
      sendProgress: NO_PROGRESS,
      dropLabel: msg("files.drop.titleThread"),
      canSend: false,
      composerAutocomplete: ws.deps.autocomplete === true,
    };
    if (threadRootId !== undefined) initial.threadRootId = threadRootId;
    if (ws.deps.api.setThreadFollow !== undefined) initial.follows = {};
    super(initial);
    this.ws = ws;
    this.draftKey = draftKey;
    this.draftFilesKey = `${DRAFT_FILES_PREFIX}${scope}`;
    this.draftAlsoSendKey = `${DRAFT_ALSO_SEND_PREFIX}${scope}`;
    this.alsoSendChecked = threadRootId !== undefined && ws.deps.storage.get(this.draftAlsoSendKey) === "1";
    this.attachmentIds = parseIds(ws.deps.storage.get(this.draftFilesKey));
    this.caret = initial.draft.length;
    const data = ws.deps.data;
    // Assign every field before subscribing: observers may notify synchronously on subscribe.
    this.uploads = data.uploads(ws.wsId);
    this.outbox = data.outbox(ws.wsId);
    if (threadRootId === undefined) this.paged = data.messages(ws.wsId, channelId);
    else this.single = data.thread(ws.wsId, channelId, threadRootId);
    const query = this.paged ?? this.single!;
    this.onDispose(query.subscribe(() => this.recompute()));
    this.onDispose(() => query.dispose());
    this.onDispose(this.outbox.subscribe(() => this.recompute()));
    this.onDispose(this.uploads.subscribe(() => this.recompute()));
    this.onDispose(
      ws.subscribe(() => {
        this.recompute();
        if (ws.state.appCommands !== this.seenCommands || ws.state.workflows !== this.seenWorkflows) {
          this.seenCommands = ws.state.appCommands;
          this.seenWorkflows = ws.state.workflows;
          this.update({ workflows: this.channelShortcuts() });
          if (this.state.autocomplete?.kind === "command") this.setMenu("composer", undefined);
          this.refreshAutocomplete();
        }
      }),
    );
    this.recompute();
    if (threadRootId !== undefined) this.loadFollow(threadRootId);
    this.onDispose(() => ws.drafts.flush(channelId, threadRootId));
    this.syncDraft();
  }

  /**
   * Drafts follow me across devices (DraftsViewModel): on open, the server's copy replaces this device's when it is
   * newer (unless I already started typing here); this device's goes up when it is newer.
   */
  private syncDraft(): void {
    const drafts = this.ws.drafts;
    if (!drafts.synced) return;
    const { channelId, threadRootId } = this.state;
    void drafts.remote(channelId, threadRootId).then((remote) => {
      if (this.isDisposed || remote === "unknown" || this.typedSinceOpen) return;
      const pick = pickDraft({ text: this.state.draft, at: drafts.localAt(channelId, threadRootId) }, remote);
      if (pick.text !== this.state.draft) {
        drafts.adopted(channelId, threadRootId, pick.at);
        if (pick.text === "") this.ws.deps.storage.remove(this.draftKey);
        else this.ws.deps.storage.set(this.draftKey, pick.text);
        this.caret = pick.text.length;
        this.update({ draft: pick.text, canSend: this.state.isMember && (pick.text.trim() !== "" || this.state.attachments.length > 0) });
      } else if (pick.push) {
        drafts.typed(channelId, threadRootId, pick.text, pick.at);
      }
    });
  }

  private query(): QueryState<MessagePage[]> & { hasOlder: boolean; fetchingOlder: boolean } {
    if (this.paged !== undefined) return this.paged.getSnapshot();
    const q = this.single!.getSnapshot();
    const paged: PagedState<MessagePage> = { ...q, data: q.data === undefined ? undefined : [q.data], hasOlder: false, fetchingOlder: false };
    return paged;
  }

  /** This conversation's ephemeral messages from the workspace (same array kept when nothing changed). */
  private syncEphemerals(): void {
    const all = this.ws.state.ephemerals ?? [];
    const mine = all.filter((e) => e.channelId === this.state.channelId && e.threadRootId === this.state.threadRootId);
    const cur = this.state.ephemerals;
    if (mine.length === cur.length && mine.every((e, i) => e === cur[i])) return;
    this.update({ ephemerals: mine });
  }

  private recompute(): void {
    this.syncEphemerals();
    const s = this.state;
    const wsState = this.ws.state;
    const cache = wsState.cache;
    const q = this.query();
    const pages = q.data;
    const now = this.ws.deps.clock.now();
    const meId = wsState.meId;
    const channel = cache === undefined ? undefined : cache.channels[s.channelId];
    const membership = cache === undefined ? undefined : cache.memberships[s.channelId];

    if (this.dividerSeq === undefined && pages !== undefined && cache !== undefined) {
      this.dividerSeq = s.threadRootId !== undefined || membership === undefined ? NO_DIVIDER : membership.lastReadSeq;
    }
    const messages = pages === undefined ? [] : flattenPages(pages);
    const pending = pendingFor(this.outbox.getSnapshot(), s.channelId, s.threadRootId);
    const opts: TimelineOptions = {
      lastReadSeq: this.dividerSeq ?? NO_DIVIDER,
      meId,
      tzOffsetMinutes: this.ws.deps.clock.tzOffsetMinutes(now),
      now,
      pending,
    };
    if (s.threadRootId !== undefined) opts.threadRootId = s.threadRootId;
    const built = buildTimeline(messages, opts);
    const items =
      s.threadRootId === undefined && cache !== undefined
        ? withThreadBroadcasts(built, messages, { meId, members: cache.members, channels: cache.channels, userGroups: cache.userGroups ?? {} })
        : built;

    const typers = activeTypers(wsState.live.typing, s.channelId, s.threadRootId, meId, now);
    const typing = cache === undefined ? undefined : typingLabel(typers.map((id) => memberName(cache, id)));
    const agentLines = cache === undefined ? [] : agentStatusLines(agentActivities(wsState.live, cache.members), s.channelId);

    const uploads = this.uploads.getSnapshot();
    const attachments = this.attachmentViews(uploads);
    const sendProgress = this.sendProgressOf(pending, uploads);
    const next: ChannelViewState = {
      ...s,
      attachments,
      sendProgress,
      dropLabel: this.dropLabel(cache),
      canSend: membership !== undefined && (s.draft.trim() !== "" || attachments.length > 0),
      status: pages !== undefined ? "ready" : q.status === "error" ? "error" : "loading",
      title: cache === undefined ? "" : channelTitle(cache, s.channelId),
      memberCount: channel === undefined ? 0 : channel.memberCount,
      isMember: membership !== undefined,
      external: false,
      items,
      hasOlder: q.hasOlder,
      loadingOlder: q.fetchingOlder,
      newCount: newMessageCount(items, meId),
      agentLines,
      composerPlaceholder: this.placeholder(cache),
      caps: cache === undefined || cache.members[meId] === undefined ? s.caps : capabilitiesFor(cache.members[meId]!, now),
      offline: !wsState.network || wsState.realtime !== "online",
    };
    const prompt = introPrompt(channel);
    if (prompt === undefined) delete next.introPrompt;
    else next.introPrompt = prompt;
    if (channel !== undefined) next.kind = channel.kind;
    if (channel !== undefined && channel.topic !== undefined) next.topic = channel.topic;
    else delete next.topic;
    if (typing === undefined) delete next.typing;
    else next.typing = typing;
    const peer = cache === undefined ? undefined : this.peer(cache);
    if (peer === undefined) delete next.peer;
    else next.peer = peer;
    if (s.threadRootId !== undefined) {
      const root = messages.find((m) => m.id === s.threadRootId);
      if (root !== undefined) next.root = root;
    }
    const labels = s.threadRootId === undefined || channel === undefined ? undefined : threadLabels(channel.kind, next.title);
    if (labels === undefined) {
      delete next.alsoSentLabel;
      delete next.threadIn;
    } else {
      next.alsoSentLabel = labels.alsoSent;
      next.threadIn = labels.header;
    }
    if (labels === undefined || membership === undefined || channel === undefined || channel.archived) delete next.alsoSend;
    else next.alsoSend = { checked: this.alsoSendChecked, label: labels.alsoSend };
    const banner = cache === undefined ? undefined : this.externalBanner(cache, messages);
    next.external = banner !== undefined;
    if (banner === undefined) delete next.externalBanner;
    else next.externalBanner = banner;
    const collapsed = cache === undefined ? [] : this.collapsedOf(items, cache, meId);
    if (collapsed.length === 0) delete next.collapsedIds;
    else next.collapsedIds = collapsed;
    const blockedId = cache === undefined || channel === undefined ? undefined : blockedDmPeer(channel, meId, cache.blockedUserIds);
    if (blockedId === undefined) delete next.blockedPeer;
    else next.blockedPeer = { userId: blockedId, name: memberName(cache!, blockedId) };
    // The message went away or can't be edited any more (deleted, I left, archived): close its editor.
    if (next.editing !== undefined && !this.editableIn(next, next.editing.messageId)) delete next.editing;
    // The message being confirmed went away or can't be deleted any more: close the confirmation.
    if (next.deleting !== undefined && !this.deletableIn(next, next.deleting.messageId)) delete next.deleting;
    this.setState(next);
    this.maybeMarkRead();
  }

  /** Draft chips for the attached uploads (ids whose upload is gone are dropped). */
  private attachmentViews(uploads: UploadEntry[]): AttachmentView[] {
    if (this.attachmentIds.length === 0) return NO_ATTACHMENTS;
    const byId = new Map(uploads.map((u) => [u.id, u]));
    const views: AttachmentView[] = [];
    for (const id of this.attachmentIds) {
      const u = byId.get(id);
      if (u === undefined) continue;
      const kind = fileKind(u.mime, u.name);
      const v: AttachmentView = {
        id,
        name: u.name,
        mime: u.mime,
        size: u.size,
        kind,
        icon: fileIcon(kind),
        phase: u.state.phase,
        progress: uploadProgress(u.state),
        label: uploadLabel(u.state),
      };
      if (u.previewUrl !== undefined) v.previewUrl = u.previewUrl;
      views.push(v);
    }
    const prev = this.state.attachments;
    const same = prev.length === views.length && prev.every((p, i) => {
      const v = views[i]!;
      return p.id === v.id && p.phase === v.phase && p.progress === v.progress && p.previewUrl === v.previewUrl;
    });
    return same ? prev : views;
  }

  private sendProgressOf(pending: PendingEntry[], uploads: UploadEntry[]): Record<string, number> {
    const waiting = pending.filter((p) => p.uploadIds !== undefined && p.uploadIds.length > 0 && p.status === "sending");
    if (waiting.length === 0) return NO_PROGRESS;
    const states: Record<string, UploadEntry["state"]> = {};
    for (const u of uploads) states[u.id] = u.state;
    const out: Record<string, number> = {};
    for (const p of waiting) out[p.message.clientId ?? p.message.id] = attachmentsProgress(p.uploadIds!, states);
    const prev = this.state.sendProgress;
    const keys = Object.keys(out);
    const same = keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === out[k]);
    return same ? prev : out;
  }

  private dropLabel(cache: WorkspaceCache | undefined): MessageDescriptor {
    if (this.state.threadRootId !== undefined) return msg("files.drop.titleThread");
    const c = cache === undefined ? undefined : cache.channels[this.state.channelId];
    if (c === undefined || cache === undefined) return msg("files.drop.titleThread");
    if (c.kind === "dm" || c.kind === "group_dm") return msg("files.drop.titleDm", { name: channelTitle(cache, c.id) });
    return msg("files.drop.title", { channel: c.name });
  }

  private collapsedOf(items: TimelineItem[], cache: WorkspaceCache, meId: string): string[] {
    const blocked = cache.blockedUserIds;
    if (blocked === undefined || blocked.length === 0) return [];
    const out: string[] = [];
    for (const it of items) if (it.type === "message" && isCollapsed(it.message, blocked, meId, this.shownBlocked)) out.push(it.message.id);
    return out;
  }

  private peer(cache: WorkspaceCache): DmPeer | undefined {
    const c = cache.channels[this.state.channelId];
    if (c === undefined || c.kind !== "dm" || c.dmUserIds === undefined) return undefined;
    const others = c.dmUserIds.filter((u) => u !== cache.meId);
    const m = cache.members[others.length === 0 ? cache.meId : others[0]!];
    if (m === undefined || others.length > 1) return undefined;
    const live = this.ws.state.live;
    const peer: DmPeer = { userId: m.userId, name: m.displayName, isAgent: m.kind === "agent" };
    if (m.avatarUrl !== undefined) peer.avatarUrl = m.avatarUrl;
    const p = m.kind === "agent" ? agentPresence(agentState(live, m).state) : live.presence[m.userId] === "active" ? "online" : live.presence[m.userId] === "away" ? "away" : undefined;
    if (p !== undefined) peer.presence = p;
    if (m.kind === "human" && memberPaused(m, this.ws.deps.clock.now())) peer.dnd = true;
    const st = activeStatus(m, this.ws.deps.clock.now());
    if (st !== undefined) peer.status = st;
    return peer;
  }

  private placeholder(cache: WorkspaceCache | undefined): MessageDescriptor {
    if (this.state.threadRootId !== undefined) return msg("composer.placeholderThread");
    const c = cache === undefined ? undefined : cache.channels[this.state.channelId];
    if (c === undefined || cache === undefined) return msg("channel.composer.placeholder", { channel: "" });
    if (c.kind === "dm" || c.kind === "group_dm") return msg("channel.composer.placeholderDm", { name: channelTitle(cache, c.id) });
    return msg("channel.composer.placeholder", { channel: c.name });
  }

  private externalBanner(cache: WorkspaceCache, messages: Message[]): MessageDescriptor | undefined {
    const c = cache.channels[this.state.channelId];
    if (c === undefined) return undefined;
    const people = new Map<string, Member>();
    for (const id of c.dmUserIds ?? []) {
      const m = cache.members[id];
      if (m !== undefined) people.set(id, m);
    }
    for (const m of messages) {
      const a = cache.members[m.authorId];
      if (a !== undefined) people.set(a.userId, a);
    }
    const externals = [...people.values()].filter(isExternal);
    if (c.external !== true && externals.length === 0) return undefined;
    const orgs = externalOrganizations(externals);
    return orgs.length === 0 ? msg("channel.externalBanner") : msg("channel.externalBannerOrgs", { orgs: orgs.join(", ") });
  }

  // ---------- Intents ----------

  /** The screen is on screen and focused: keep the read marker at the newest message. */
  setVisible(visible: boolean): void {
    this.visible = visible;
    this.maybeMarkRead();
  }

  private maybeMarkRead(): void {
    if (!this.visible || this.holdRead || this.state.threadRootId !== undefined) return;
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    const c = cache.channels[this.state.channelId];
    const ms = cache.memberships[this.state.channelId];
    if (c === undefined || ms === undefined) return;
    if (c.lastMessageSeq > ms.lastReadSeq && c.lastMessageSeq > this.markedSeq) {
      this.markedSeq = c.lastMessageSeq;
      this.ws.deps.data.markRead(this.ws.wsId, this.state.channelId, c.lastMessageSeq);
    }
  }

  /** `caret` = where the composer's caret is after the edit; absent = estimated (typing moves it with the text). */
  setDraft(text: string, caret?: number): void {
    const changed = text !== this.state.draft;
    this.caret = caret ?? estimateCaret(this.state.draft, text, this.caret);
    this.update({ draft: text, canSend: this.state.isMember && (text.trim() !== "" || this.state.attachments.length > 0) });
    if (text === "") this.ws.deps.storage.remove(this.draftKey);
    else this.ws.deps.storage.set(this.draftKey, text);
    if (changed) {
      this.typedSinceOpen = true;
      this.ws.drafts.typed(this.state.channelId, this.state.threadRootId, text);
    }
    if (text.startsWith("/")) this.ws.loadAppCommands();
    this.refreshAutocomplete();
    if (text.trim() === "") return;
    const now = this.ws.deps.clock.now();
    if (shouldSendTyping(this.lastTypingSentAt, now)) {
      this.lastTypingSentAt = now;
      this.ws.sendTyping(this.state.channelId, this.state.threadRootId);
    }
  }

  /**
   * Sends the draft (text = the files' caption). Works offline: the message is queued and shown with a pending status;
   * with attachments it goes out once they are uploaded.
   */
  send(): void {
    const text = this.state.draft.trim();
    const attachments = this.state.attachments;
    if ((text === "" && attachments.length === 0) || !this.state.isMember) return;
    if (attachments.length === 0 && isRemindCommand(text)) {
      this.runRemind(text);
      return;
    }
    const invocation = attachments.length === 0 ? slashInvocation(text) : undefined;
    if (invocation !== undefined && this.ws.state.appCommands?.some((c) => c.command === invocation.command) === true) {
      this.runAppCommand(text, invocation.command, invocation.text);
      return;
    }
    const range = this.draftBroadcastRange(text);
    if (range !== undefined) {
      void this.askBroadcast(range, text);
      return;
    }
    this.sendNow();
  }

  /** The draft's @channel / @everyone / @here when it would notify the channel (a channel message, or a reply also sent there). */
  private draftBroadcastRange(text: string): BroadcastRange | undefined {
    const cache = this.ws.state.cache;
    const c = cache?.channels[this.state.channelId];
    if (cache === undefined || c === undefined || c.kind === "dm" || c.kind === "group_dm") return undefined;
    if (this.state.threadRootId !== undefined && this.state.alsoSend?.checked !== true) return undefined;
    return draftBroadcast(text, { meId: this.ws.state.meId, members: cache.members, channels: cache.channels });
  }

  /** Counts who the broadcast reaches (the member list when online) and asks first when it's more than 6 people. */
  private async askBroadcast(range: BroadcastRange, text: string): Promise<void> {
    if (this.sendingBroadcast) return;
    this.sendingBroadcast = true;
    let memberIds: string[] | undefined;
    if (this.ws.state.network) {
      try {
        memberIds = (await this.ws.deps.data.channelMembers(this.ws.wsId, this.state.channelId)).members.map((m) => m.userId);
      } catch {
        memberIds = undefined;
      }
    }
    this.sendingBroadcast = false;
    // The draft changed (or the screen closed) while counting: nothing to ask about.
    if (this.isDisposed || this.state.draft.trim() !== text) return;
    const cache = this.ws.state.cache;
    const warning =
      cache === undefined
        ? undefined
        : broadcastWarning(range, {
            meId: this.ws.state.meId,
            members: cache.members,
            memberCount: cache.channels[this.state.channelId]?.memberCount ?? this.state.memberCount,
            ...(memberIds === undefined ? {} : { memberIds }),
            presence: this.ws.state.live.presence,
          });
    if (warning === undefined) this.sendNow();
    else this.update({ broadcastConfirm: warning });
  }

  /** "Send anyway" in the broadcast confirmation. */
  confirmBroadcast(): void {
    if (this.state.broadcastConfirm === undefined) return;
    this.update({ broadcastConfirm: undefined });
    this.sendNow();
  }

  /** Cancel in the broadcast confirmation: the draft stays to edit. */
  cancelBroadcast(): void {
    if (this.state.broadcastConfirm !== undefined) this.update({ broadcastConfirm: undefined });
  }

  private sendNow(): void {
    const text = this.state.draft.trim();
    const attachments = this.state.attachments;
    if ((text === "" && attachments.length === 0) || !this.state.isMember) return;
    const input: SendInput = {
      clientId: this.ws.deps.ids.next(),
      channelId: this.state.channelId,
      text,
      createdAt: this.ws.deps.clock.now(),
    };
    if (this.state.threadRootId !== undefined) input.threadRootId = this.state.threadRootId;
    if (this.state.alsoSend !== undefined && this.state.alsoSend.checked) input.alsoSendToChannel = true;
    if (attachments.length > 0) {
      const byId = new Map(this.uploads.getSnapshot().map((u) => [u.id, u]));
      input.uploadIds = attachments.map((a) => a.id);
      input.files = attachments.map((a) => optimisticFile(a, byId.get(a.id)));
    }
    this.ws.deps.data.send(this.ws.wsId, input);
    // Replying follows the thread (as the server does).
    if (input.threadRootId !== undefined && this.state.follows !== undefined) this.update({ follows: { ...this.state.follows, [input.threadRootId]: true } });
    this.lastTypingSentAt = undefined;
    this.setAttachments([]);
    this.storeAlsoSend(false);
    this.setDraft("");
    // The chips belong to the queued message now (nothing else may change while offline).
    this.recompute();
  }

  /**
   * Where `/remind` reminders go (the workspace's LaterViewModel.addTextReminder, so Later shows them at once); unset,
   * straight to the API.
   */
  setReminderSink(sink: ((text: string, at: number, opts: ReminderOptions) => Promise<unknown>) | undefined): void {
    this.reminderSink = sink;
  }

  /** `/remind me <what> <when>`: adds a text reminder (the draft clears) or says what's wrong (the draft stays). */
  /**
   * An app's slash command (contracts apps.ts): the app answers through the API — ephemeral (kept here, only I see
   * it), posted in the channel (arrives as a message), or later via its response_url (`app.ephemeral` / a message).
   * The draft clears at once and comes back if the request fails.
   */
  private runAppCommand(draft: string, command: string, text: string): void {
    const run = this.ws.deps.api.runCommand;
    if (run === undefined) return;
    this.setDraft("");
    this.update({ runningCommand: command });
    const req: { channelId: string; command: string; text: string; threadRootId?: string; clientToken: string } = { channelId: this.state.channelId, command, text, clientToken: this.ws.modal.newClientToken() };
    if (this.state.threadRootId !== undefined) req.threadRootId = this.state.threadRootId;
    run.call(this.ws.deps.api, this.ws.wsId, req).then(
      (out) => {
        this.clearRunning();
        if (out.ephemeral !== undefined) this.ws.addEphemeral(out.ephemeral);
        if (out.views !== undefined) this.ws.modal.apply(out.views, true);
      },
      () => {
        this.clearRunning();
        if (this.state.draft === "") this.setDraft(draft);
        this.update({ commandNotice: { kind: "error", message: msg("composer.appCommand.failed", { command }) } });
      },
    );
  }

  /** This channel's link workflows, by name (its workflows menu and the `/` menu). */
  private channelShortcuts(): WorkflowShortcut[] {
    return channelWorkflows(this.ws.state.workflows ?? [], this.state.channelId).map((w) => ({ id: w.id, name: w.definition.name, icon: w.definition.icon, description: w.definition.description }));
  }

  /** The channel's workflows menu opened: refresh the list. */
  loadWorkflows(): void {
    this.ws.loadWorkflows(true);
  }

  /** Runs one of the channel's link workflows (its form opens here); a failure shows under the composer. */
  runWorkflow(workflowId: string): void {
    const name = this.ws.state.workflows?.find((w) => w.id === workflowId)?.definition.name ?? "";
    this.update({ runningWorkflow: workflowId });
    this.ws.runWorkflow(workflowId, this.state.channelId).then(
      () => {
        if (!this.isDisposed && this.state.runningWorkflow === workflowId) this.update({ runningWorkflow: undefined });
      },
      () => {
        if (this.isDisposed) return;
        this.update({ runningWorkflow: undefined, commandNotice: { kind: "error", message: msg("workflow.run.failed", { name }) } });
      },
    );
  }

  private clearRunning(): void {
    if (this.isDisposed || this.state.runningCommand === undefined) return;
    const { runningCommand: _r, ...rest } = this.state;
    this.setState(rest);
  }

  /**
   * A Block Kit button on a message (or an ephemeral one) was pressed: the app gets a `block_actions` payload
   * (blockAction). A button with a URL just opens it (`openUrl`, the platform's link opener) and still tells the app.
   * Any ephemeral answer is kept here.
   */
  pressButton(messageId: string, blockId: string, button: KitButtonNode, openUrl?: (url: string) => void, values?: KitValues, blocks?: readonly KitBlock[]): void {
    if (button.url !== undefined) openUrl?.(button.url);
    const req: BlockActionRequest = { actionId: button.actionId, blockId };
    if (button.value !== undefined) req.value = button.value;
    this.sendBlockAction(messageId, req, values, blocks, button.url === undefined);
  }

  /**
   * A select, picker, checkbox group, radio group or overflow menu on a message (or an ephemeral one) changed: the app
   * gets `block_actions` with the new value and the message's `state.values`. `values` are the message's current
   * values (the platform keeps them per message, starting from `initialKitValues`).
   */
  changeElement(messageId: string, blockId: string, element: KitValueNode | KitOverflowNode, value: string[], values: KitValues, blocks: readonly KitBlock[]): void {
    const req: BlockActionRequest = { actionId: element.actionId, blockId };
    if (element.kind === "overflow") {
      const o = element.options.find((x) => x.value === value[0]);
      if (o === undefined) return;
      req.selected = { type: "overflow", selected_option: { text: { type: "plain_text", text: o.label }, value: o.value } };
    } else req.selected = kitStateValue(element, value, this.ws.kitLabels[blockId]?.[element.actionId]);
    this.sendBlockAction(messageId, req, element.kind === "overflow" ? values : { ...values, [blockId]: { ...values[blockId], [element.actionId]: value } }, blocks, true);
  }

  private sendBlockAction(messageId: string, req: BlockActionRequest, values: KitValues | undefined, blocks: readonly KitBlock[] | undefined, notice: boolean): void {
    const act = this.ws.deps.api.blockAction;
    if (act === undefined) return;
    if (values !== undefined && blocks !== undefined) req.values = kitState(renderKit(blocks), values, this.ws.kitLabels);
    req.clientToken = this.ws.modal.newClientToken();
    act.call(this.ws.deps.api, this.ws.wsId, messageId, req).then(
      (out) => {
        if (out.ephemeral !== undefined) this.ws.addEphemeral(out.ephemeral);
        if (out.views !== undefined) this.ws.modal.apply(out.views, true);
      },
      () => {
        if (notice) this.update({ commandNotice: { kind: "error", message: msg("composer.appAction.failed") } });
      },
    );
  }

  dismissEphemeral(id: string): void {
    this.ws.dismissEphemeral(id);
  }

  private runRemind(draft: string): void {
    const now = this.ws.deps.clock.now();
    const parsed = parseRemind(draft, now, this.ws.deps.clock.tzOffsetMinutes(now));
    if (!parsed.ok) {
      this.update({ commandNotice: { kind: "error", message: msg(parsed.error === "target" ? "composer.remind.target" : parsed.error === "past" ? "composer.remind.past" : "composer.remind.usage") } });
      return;
    }
    const opts: ReminderOptions = {};
    if (parsed.repeat !== undefined) opts.repeat = parsed.repeat;
    let who: string | undefined;
    const cache = this.ws.state.cache;
    if (parsed.target?.kind === "user" && parsed.target.userId !== this.ws.state.meId) {
      opts.forUserId = parsed.target.userId;
      who = cache === undefined ? undefined : `@${memberName(cache, parsed.target.userId)}`;
    } else if (parsed.target?.kind === "channel") {
      opts.forChannelId = parsed.target.channelId;
      const c = cache?.channels[parsed.target.channelId];
      who = c === undefined || cache === undefined ? undefined : c.kind === "dm" || c.kind === "group_dm" ? channelTitle(cache, c.id) : `#${c.name}`;
    }
    const sink = this.reminderSink ?? ((text: string, at: number, o: ReminderOptions) => this.ws.deps.api.saveLater(this.ws.wsId, { text, remindAt: at, ...o }));
    this.setDraft("");
    const notice: CommandNotice = { kind: "reminded", text: parsed.text, at: parsed.at };
    if (who !== undefined) notice.who = who;
    if (parsed.repeat !== undefined) notice.repeats = true;
    sink(parsed.text, parsed.at, opts).then(
      () => this.update({ commandNotice: notice }),
      () => {
        if (this.state.draft === "") this.setDraft(draft);
        this.update({ commandNotice: { kind: "error", message: msg("composer.remind.failed") } });
      },
    );
  }

  /**
   * Where an emoji picked from the composer's ":" autocomplete is counted for "Frequently used" (the workspace's
   * EmojiUsageViewModel.pick); unset, it is reported straight to the API. The sink may return the emoji to insert
   * instead (in my skin tone).
   */
  setEmojiSink(sink: ((emoji: string) => string | void) | undefined): void {
    this.emojiSink = sink;
  }

  /** Where "Schedule message" goes (the workspace's ScheduledViewModel.schedule); unset, straight to the API. */
  setScheduleSink(sink: ((req: ScheduleRequest) => Promise<unknown>) | undefined): void {
    this.scheduleSink = sink;
  }

  /**
   * Whether the draft can be scheduled: text and/or files whose uploads finished (the server holds them until the
   * message goes out), not a `/remind`, and I may post here.
   */
  get canSchedule(): boolean {
    const a = this.state.attachments;
    if (!this.state.isMember || isRemindCommand(this.state.draft)) return false;
    if (this.state.draft.trim() === "" && a.length === 0) return false;
    return this.scheduledFileIds() !== undefined;
  }

  /** The server file ids of the attachments, or undefined while any of them is still uploading (or failed). */
  private scheduledFileIds(): string[] | undefined {
    const byId = new Map(this.uploads.getSnapshot().map((u) => [u.id, u]));
    const out: string[] = [];
    for (const id of this.attachmentIds) {
      const file = byId.get(id)?.state.file;
      if (file === undefined) return undefined;
      out.push(file.id);
    }
    return out;
  }

  /**
   * "Schedule message" from the send menu: the draft is posted by the server at `at` (logic/scheduled). The draft
   * clears now and comes back if the server refuses.
   */
  scheduleDraft(at: number): void {
    if (!this.canSchedule) return;
    const now = this.ws.deps.clock.now();
    const problem = sendAtProblem(at, now);
    if (problem !== undefined) {
      this.update({ commandNotice: { kind: "error", message: msg(problem === "past" ? "schedule.past" : "schedule.tooFar") } });
      return;
    }
    const draft = this.state.draft;
    const req: ScheduleRequest = { channelId: this.state.channelId, text: draft.trim(), sendAt: at };
    const fileIds = this.scheduledFileIds() ?? [];
    const attached = this.attachmentIds;
    if (fileIds.length > 0) req.fileIds = fileIds;
    if (this.state.threadRootId !== undefined) req.threadRootId = this.state.threadRootId;
    if (this.state.alsoSend?.checked === true) req.alsoSendToChannel = true;
    const sink = this.scheduleSink ?? ((r: ScheduleRequest) => this.ws.deps.api.createScheduled(this.ws.wsId, r));
    this.setDraft("");
    this.setAlsoSend(false);
    if (attached.length > 0) {
      this.setAttachments([]);
      this.recompute();
    }
    sink(req).then(
      () => {
        // The server holds the files now: the local upload entries go.
        for (const id of attached) this.ws.deps.data.removeUpload(this.ws.wsId, id);
        this.update({ commandNotice: { kind: "scheduled", at } });
      },
      () => {
        if (this.state.draft === "") this.setDraft(draft);
        if (attached.length > 0 && this.attachmentIds.length === 0) {
          this.setAttachments(attached);
          this.recompute();
        }
        this.update({ commandNotice: { kind: "error", message: msg("schedule.failed") } });
      },
    );
  }

  dismissCommandNotice(): void {
    if (this.state.commandNotice === undefined) return;
    const { commandNotice: _n, ...rest } = this.state;
    this.setState(rest);
  }

  /** The thread composer's "Also send to #channel" checkbox (ignored where it isn't offered). */
  setAlsoSend(checked: boolean): void {
    const offer = this.state.alsoSend;
    if (offer === undefined || offer.checked === checked) return;
    this.storeAlsoSend(checked);
    this.update({ alsoSend: { ...offer, checked } });
  }

  /**
   * A sticker or GIF from this thread's picker is going out: whether it also goes to the channel (the "Also send"
   * checkbox, where offered). Clears the checkbox, like a text reply does.
   */
  takeAlsoSend(): boolean {
    const offer = this.state.alsoSend;
    if (offer === undefined || !offer.checked) return false;
    this.setAlsoSend(false);
    return true;
  }

  private storeAlsoSend(checked: boolean): void {
    this.alsoSendChecked = checked;
    if (checked) this.ws.deps.storage.set(this.draftAlsoSendKey, "1");
    else this.ws.deps.storage.remove(this.draftAlsoSendKey);
  }

  /**
   * Attaches picked, dropped or pasted files to the draft and starts uploading them (they wait on this device while
   * offline). Files already attached, over 1 GB, or past the 10-file limit are skipped with a notice.
   */
  addFiles(files: PickedFile[]): void {
    if (!this.state.isMember || files.length === 0) return;
    const existing = this.attachmentIds
      .map((id) => this.uploads.getSnapshot().find((u) => u.id === id))
      .filter((u): u is UploadEntry => u !== undefined)
      .map((u) => ({ name: u.name, size: u.size, lastModified: u.lastModified }));
    const keep = dedupeFiles(existing, files);
    let notice: MessageDescriptor | undefined =
      keep.length < files.length ? msg("files.attach.duplicate", { name: files.find((_, i) => !keep.includes(i))!.name }) : undefined;
    const ids = this.attachmentIds.slice();
    for (const i of keep) {
      const f = files[i]!;
      const problem = attachProblem(f, ids.length);
      if (problem !== null) {
        notice = problem;
        continue;
      }
      ids.push(this.ws.deps.data.uploadFile(this.ws.wsId, f, { type: "channel", channelId: this.state.channelId }).id);
    }
    this.setAttachments(ids);
    if (notice === undefined) this.dismissAttachNotice();
    else this.update({ attachNotice: notice });
    this.recompute();
  }

  /** Removes a chip (and cancels its upload). */
  removeAttachment(id: string): void {
    if (!this.attachmentIds.includes(id)) return;
    this.setAttachments(this.attachmentIds.filter((x) => x !== id));
    this.ws.deps.data.removeUpload(this.ws.wsId, id);
    this.recompute();
  }

  retryAttachment(id: string): void {
    this.ws.deps.data.retryUpload(this.ws.wsId, id);
  }

  dismissAttachNotice(): void {
    if (this.state.attachNotice === undefined) return;
    const { attachNotice: _n, ...rest } = this.state;
    this.setState(rest);
  }

  private setAttachments(ids: string[]): void {
    this.attachmentIds = ids;
    if (ids.length === 0) this.ws.deps.storage.remove(this.draftFilesKey);
    else this.ws.deps.storage.set(this.draftFilesKey, JSON.stringify(ids));
  }

  retry(clientId: string): void {
    this.ws.deps.data.retrySend(this.ws.wsId, clientId);
  }

  discard(clientId: string): void {
    this.ws.deps.data.discardSend(this.ws.wsId, clientId);
  }

  /** "Show" on a collapsed message from someone I blocked: reveals it in this view only. */
  showBlocked(messageId: string): void {
    if (this.shownBlocked.includes(messageId)) return;
    this.shownBlocked = [...this.shownBlocked, messageId];
    this.recompute();
  }

  /** "Report message" in this message's menu (not my own, not deleted, already sent). */
  canReport(messageId: string): boolean {
    const item = this.messageItem(this.state, messageId);
    return item !== undefined && item.status === "sent" && canReport(item.message, this.ws.state.meId);
  }

  /** The author "Block @name" in this message's menu can block (another person; not apps or agents), if any. */
  blockableAuthor(messageId: string): { userId: string; name: string; blocked: boolean } | undefined {
    const item = this.messageItem(this.state, messageId);
    const cache = this.ws.state.cache;
    if (item === undefined || cache === undefined) return undefined;
    const author = cache.members[item.message.authorId];
    if (author === undefined || !canBlock(author, this.ws.state.meId) || item.message.app !== undefined) return undefined;
    return { userId: author.userId, name: author.displayName, blocked: (cache.blockedUserIds ?? []).includes(author.userId) };
  }

  /**
   * Jumps to a message of this conversation (a link, search hit, pin, Activity): when it is older than what's loaded,
   * history pages back to it (in big pages, straight to its seq when the server says it) so its row can be shown in
   * context. Resolves true once it is loaded; false when it isn't in this timeline (a thread reply, gone, too far).
   */
  async reveal(messageId: string): Promise<boolean> {
    const loaded = (): boolean => this.query().data?.some((p) => p.messages.some((m) => m.id === messageId)) === true;
    if (loaded()) return true;
    const paged = this.paged;
    if (paged === undefined) return false;
    let target: number | undefined;
    const get = this.ws.deps.api.getMessage;
    if (get !== undefined) {
      try {
        const m = await get.call(this.ws.deps.api, this.ws.wsId, messageId);
        if (m.channelId !== this.state.channelId || (m.threadRootId !== undefined && m.alsoSentToChannel !== true)) return false;
        target = m.seq;
      } catch {
        // Offline or not found: page back blindly (bounded).
      }
    }
    for (let i = 0; i < REVEAL_MAX_PAGES && !this.isDisposed; i++) {
      if (loaded()) return true;
      const q = this.query();
      if (!q.hasOlder) return false;
      let oldest: number | undefined;
      for (const p of q.data ?? []) for (const m of p.messages) if (oldest === undefined || m.seq < oldest) oldest = m.seq;
      if (target !== undefined && oldest !== undefined && oldest <= target) return loaded();
      await paged.fetchOlder(revealPageSize(oldest, target));
    }
    return loaded();
  }

  loadOlder(): void {
    if (this.paged !== undefined && this.state.hasOlder && !this.state.loadingOlder) void this.paged.fetchOlder();
  }

  refresh(): void {
    void (this.paged !== undefined ? this.paged.refresh() : this.single!.refresh());
  }

  toggleReaction(messageId: string, emoji: string): void {
    const item = this.state.items.find((i) => i.type === "message" && i.message.id === messageId);
    if (item === undefined || item.type !== "message") return;
    const on = reactionToggle(item.message, this.ws.state.meId, emoji).add;
    this.ws.deps.data.setReaction(this.ws.wsId, this.state.channelId, messageId, emoji, on);
  }

  edit(messageId: string, text: string): void {
    if (text.trim() !== "") this.ws.deps.data.editMessage(this.ws.wsId, this.state.channelId, messageId, text.trim());
  }

  // ---------- Editing my messages in place ----------

  private editPlace(s: ChannelViewState): EditPlace {
    const c = this.ws.state.cache?.channels[s.channelId];
    return { member: s.isMember, archived: c !== undefined && c.archived };
  }

  // ---------- Thread follows ----------

  /** The thread a message belongs to (its root's id; a top-level message is its own root). */
  private rootOf(messageId: string): string {
    if (this.state.threadRootId !== undefined) return this.state.threadRootId;
    return this.messageItem(this.state, messageId)?.message.threadRootId ?? messageId;
  }

  /**
   * Whether I follow the message's thread (undefined: following isn't supported). Until the server said so (a thread
   * asks on open, a toggle sets it), the default rule answers: I wrote the root or replied to it.
   */
  following(messageId: string): boolean | undefined {
    const follows = this.state.follows;
    if (follows === undefined) return undefined;
    const rootId = this.rootOf(messageId);
    const known = follows[rootId];
    if (known !== undefined) return known;
    const meId = this.ws.state.meId;
    const root = this.state.root?.id === rootId ? this.state.root : this.messageItem(this.state, rootId)?.message;
    return root !== undefined && (root.authorId === meId || root.replyUserIds.includes(meId));
  }

  /** The message menu opened (or a thread): learn whether I follow its thread, once. */
  loadFollow(messageId: string): void {
    const api = this.ws.deps.api;
    const rootId = this.rootOf(messageId);
    if (api.threadFollow === undefined || this.state.follows === undefined || rootId in this.state.follows) return;
    api.threadFollow(this.ws.wsId, rootId).then(
      (on) => {
        if (!this.isDisposed && this.state.follows !== undefined && !(rootId in this.state.follows)) this.update({ follows: { ...this.state.follows, [rootId]: on } });
      },
      () => undefined,
    );
  }

  /** "Get notified about new replies" (true) / "Turn off notifications for replies" (false): here at once, then the server. */
  setFollow(messageId: string, following: boolean): void {
    const api = this.ws.deps.api;
    const follows = this.state.follows;
    if (api.setThreadFollow === undefined || follows === undefined) return;
    const rootId = this.rootOf(messageId);
    const before = follows[rootId];
    this.update({ follows: { ...follows, [rootId]: following } });
    api.setThreadFollow(this.ws.wsId, rootId, following).catch(() => {
      if (this.isDisposed || this.state.follows === undefined) return;
      const rest = { ...this.state.follows };
      if (before === undefined) delete rest[rootId];
      else rest[rootId] = before;
      this.update({ follows: rest });
    });
  }

  private messageItem(s: ChannelViewState, messageId: string): Extract<TimelineItem, { type: "message" }> | undefined {
    const item = s.items.find((i) => i.type === "message" && i.message.id === messageId);
    return item === undefined || item.type !== "message" ? undefined : item;
  }

  private editableIn(s: ChannelViewState, messageId: string): boolean {
    const item = this.messageItem(s, messageId);
    if (item === undefined) return false;
    return canEditMessage(item.message, this.ws.state.meId, item.status === "sent", this.editPlace(s));
  }

  /** The row may offer "Edit message": mine, sent, not deleted, not a sticker or GIF, and I can still post here. */
  canEdit(messageId: string): boolean {
    return this.editableIn(this.state, messageId);
  }

  /**
   * Opens the in-place editor on a message of mine, prefilled with its text (another open editor is dropped). The
   * editor takes the focus (`editing.selection`, caret at the end) and the main composer's menu closes.
   */
  startEdit(messageId: string): void {
    if (!this.canEdit(messageId)) return;
    const item = this.messageItem(this.state, messageId);
    if (item === undefined) return;
    const draft = item.message.text;
    this.editCaret = draft.length;
    this.editCollapsed = true;
    this.editDismissed = undefined;
    const seq = this.state.editing?.selection === undefined ? 1 : this.state.editing.selection.seq + 1;
    const { autocomplete: _closed, ...rest } = this.state;
    this.setState({ ...rest, editing: { messageId, draft, canSave: draft.trim() !== "", selection: { start: draft.length, end: draft.length, seq } } });
  }

  /** `caret` = where the editor's caret is after the edit; absent = estimated (typing moves it with the text). */
  setEditDraft(text: string, caret?: number): void {
    const e = this.state.editing;
    if (e === undefined || e.draft === text) return;
    this.editCaret = caret ?? estimateCaret(e.draft, text, this.editCaret);
    this.update({ editing: { ...e, draft: text, canSave: text.trim() !== "" } });
    this.refreshEditAutocomplete();
  }

  /** The editor's caret or selection moved; UTF-16 offsets in its draft. */
  setEditSelection(start: number, end: number): void {
    if (this.state.editing === undefined) return;
    if (this.editCaret === end && this.editCollapsed === (start === end)) return;
    this.editCaret = end;
    this.editCollapsed = start === end;
    this.refreshEditAutocomplete();
  }

  /**
   * Saves the edit (Enter, Save). An empty draft is refused (the editor stays open; deleting is its own action);
   * unchanged text just closes the editor. The edit is applied optimistically and works offline (queued).
   */
  saveEdit(): void {
    const e = this.state.editing;
    if (e === undefined) return;
    const item = this.messageItem(this.state, e.messageId);
    if (item === undefined) {
      this.cancelEdit();
      return;
    }
    const outcome = editOutcome(item.message.text, e.draft);
    if (outcome.type === "refuse") return;
    if (outcome.type === "save") this.ws.deps.data.editMessage(this.ws.wsId, this.state.channelId, e.messageId, outcome.text);
    this.closeEditor();
  }

  /** Escape or Cancel: drops the edit; the message keeps its text. */
  cancelEdit(): void {
    if (this.state.editing === undefined) return;
    this.closeEditor();
  }

  /**
   * The editor's keys: its autocomplete menu first (like the main composer's), then Escape cancels (Enter saves
   * through the field's submit). Returns true when taken.
   */
  editKey(key: ComposerKey): boolean {
    if (this.state.editing === undefined) return false;
    if (this.menuKey("edit", key)) return true;
    if (key !== "escape") return false;
    this.cancelEdit();
    return true;
  }

  /** ↑ in an empty composer (Slack): edits my newest editable message here. False when there is none. */
  editLast(): boolean {
    const items = this.state.items;
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const it = items[i]!;
      if (it.type !== "message" || !this.canEdit(it.message.id)) continue;
      this.startEdit(it.message.id);
      return true;
    }
    return false;
  }

  /**
   * The main composer's keys: the autocomplete menu first, then ↑ in an empty composer (no text, no files) edits my
   * last message. Returns true when taken (the field must do nothing else).
   */
  composerKey(key: ComposerKey): boolean {
    if (this.autocompleteKey(key)) return true;
    if (key !== "up" || this.state.draft !== "" || this.state.attachments.length > 0) return false;
    return this.editLast();
  }

  /** Closes the editor and puts the caret back in the main composer. */
  private closeEditor(): void {
    const { editing: _closed, ...rest } = this.state;
    this.setState(rest);
    this.editDismissed = undefined;
    this.focusComposer(Math.min(this.caret, this.state.draft.length));
  }

  // ---------- Deleting messages ----------

  private deletableIn(s: ChannelViewState, messageId: string): boolean {
    const item = this.messageItem(s, messageId);
    const cache = this.ws.state.cache;
    if (item === undefined || cache === undefined) return false;
    return canDeleteMessage(item.message, cache.members[this.ws.state.meId], item.status === "sent", this.ws.deps.clock.now());
  }

  /** The row may offer "Delete message": mine, or anyone's for an owner or admin; sent and not deleted already. */
  canDelete(messageId: string): boolean {
    return this.deletableIn(this.state, messageId);
  }

  /** "Delete message" (row menu): opens the confirmation (`deleting`) — nothing is deleted yet. */
  askDelete(messageId: string): void {
    if (!this.canDelete(messageId)) return;
    const item = this.messageItem(this.state, messageId);
    const cache = this.ws.state.cache;
    if (item === undefined || cache === undefined) return;
    const m = item.message;
    const meId = this.ws.state.meId;
    this.update({
      deleting: {
        messageId,
        authorName: m.app !== undefined ? m.app.name : memberName(cache, m.authorId),
        preview: messagePreview(m, { meId, members: cache.members, channels: cache.channels, userGroups: cache.userGroups ?? {} }, DELETE_PREVIEW_MAX),
        fileCount: m.files.length,
        others: m.authorId !== meId,
        hasReplies: m.replyCount > 0,
      },
    });
  }

  /**
   * Deletes the message being confirmed. Optimistic and offline-safe (queued): it disappears at once — a thread root
   * with replies stays as "This message was deleted." — and its files are deleted with it on the server.
   */
  confirmDelete(): void {
    const d = this.state.deleting;
    if (d === undefined) return;
    const { deleting: _closed, ...rest } = this.state;
    this.setState(rest);
    if (!this.canDelete(d.messageId)) return;
    if (this.state.editing !== undefined && this.state.editing.messageId === d.messageId) this.closeEditor();
    this.ws.deps.data.deleteMessage(this.ws.wsId, this.state.channelId, d.messageId);
  }

  /** Cancel, Escape or a click outside the confirmation: nothing is deleted. */
  cancelDelete(): void {
    if (this.state.deleting === undefined) return;
    const { deleting: _closed, ...rest } = this.state;
    this.setState(rest);
  }

  /** Deletes without asking (callers that confirmed elsewhere). */
  remove(messageId: string): void {
    this.ws.deps.data.deleteMessage(this.ws.wsId, this.state.channelId, messageId);
  }

  // ---------- Message extras: copy link, pin, mark unread (contracts messageExtras.ts) ----------

  /**
   * App path of the message's link (logic/shell `messageUrl`: the conversation, or the thread for a reply, with
   * `?m=`). The platform prefixes its origin and copies it. Undefined while the message isn't sent yet.
   */
  messageLink(messageId: string): string | undefined {
    const item = this.messageItem(this.state, messageId);
    const slug = this.ws.state.cache?.workspace.slug;
    if (item === undefined || slug === undefined || item.status !== "sent") return undefined;
    const m = item.message;
    return messageUrl(slug, m.channelId, m.id, m.threadRootId);
  }

  /** "Pin to channel" / "Unpin": members of a live conversation, on sent messages (not stickers' tombstones). */
  canPin(messageId: string): boolean {
    const item = this.messageItem(this.state, messageId);
    const c = this.ws.state.cache?.channels[this.state.channelId];
    return item !== undefined && item.status === "sent" && !item.message.deleted && this.state.isMember && c !== undefined && !c.archived;
  }

  isPinned(messageId: string): boolean {
    return this.messageItem(this.state, messageId)?.message.pinnedBy !== undefined;
  }

  /** Pins or unpins (optimistic; rolled back when the server refuses). An open Pinned list refreshes after. */
  togglePin(messageId: string): void {
    if (!this.canPin(messageId)) return;
    const on = !this.isPinned(messageId);
    this.ws.deps.data.setPinned(this.ws.wsId, this.state.channelId, messageId, on).then(
      () => {
        if (!this.isDisposed && this.state.pins !== undefined) this.loadPins();
      },
      () => undefined,
    );
  }

  /** Link previews: the author of a sent message may remove them (Slack). */
  canHideUnfurl(messageId: string): boolean {
    const item = this.messageItem(this.state, messageId);
    return item !== undefined && item.status === "sent" && !item.message.deleted && item.message.authorId === this.ws.state.meId;
  }

  /** Removes one link's preview of my message; the change arrives as message.updated. */
  hideUnfurl(messageId: string, url: string): void {
    if (!this.canHideUnfurl(messageId)) return;
    this.ws.deps.api.hideUnfurl(this.ws.wsId, messageId, url).catch(() => undefined);
  }

  /** Opens the Pinned list and loads it. */
  openPins(): void {
    this.update({ pins: { status: "loading", messages: this.state.pins?.messages ?? [] } });
    this.loadPins();
  }

  closePins(): void {
    if (this.state.pins === undefined) return;
    const { pins: _closed, ...rest } = this.state;
    this.setState(rest);
  }

  private loadPins(): void {
    this.ws.deps.api.listPins(this.ws.wsId, this.state.channelId).then(
      (messages) => {
        if (!this.isDisposed && this.state.pins !== undefined) this.update({ pins: { status: "ready", messages } });
      },
      () => {
        if (!this.isDisposed && this.state.pins !== undefined) this.update({ pins: { status: "error", messages: this.state.pins.messages } });
      },
    );
  }

  /** "Mark unread": channel-level messages in the conversation view (thread replies have no read marker yet). */
  canMarkUnread(messageId: string): boolean {
    const item = this.messageItem(this.state, messageId);
    return this.state.threadRootId === undefined && this.state.isMember && item !== undefined && item.status === "sent" && item.message.seq > 0;
  }

  /**
   * Marks the conversation unread from this message: the NEW divider moves above it, the sidebar shows it unread, and
   * it stays unread while I'm here (it is read again when I come back to it).
   */
  markUnread(messageId: string): void {
    if (!this.canMarkUnread(messageId)) return;
    const seq = this.messageItem(this.state, messageId)!.message.seq;
    this.holdRead = true;
    this.markedSeq = 0;
    this.dividerSeq = seq - 1;
    this.recompute();
    this.ws.deps.data.markUnread(this.ws.wsId, this.state.channelId, seq).catch(() => undefined);
  }

  /** Hides the "new" divider (e.g. after jumping to the newest message). */
  clearNewDivider(): void {
    this.dividerSeq = NO_DIVIDER;
    this.recompute();
  }

  // ---------- Composer autocomplete (logic/autocomplete) ----------
  // Two fields share it: the main composer ("composer": state.autocomplete) and the in-place editor ("edit":
  // state.editing.autocomplete, no slash commands — an edit can't run one).

  /** The composer's caret or selection moved (typing, clicks, arrow keys); UTF-16 offsets in the draft. */
  setComposerSelection(start: number, end: number): void {
    if (this.caret === end && this.selectionCollapsed === (start === end)) return;
    this.caret = end;
    this.selectionCollapsed = start === end;
    this.refreshAutocomplete();
  }

  /**
   * A composer key. Returns true when the menu took it (the field must do nothing else): ↑/↓ move, Enter/Tab insert
   * the highlighted row, Escape closes the menu until the typed token ends. Enter on "No matches" closes and sends.
   */
  autocompleteKey(key: ComposerKey): boolean {
    return this.menuKey("composer", key);
  }

  moveAutocomplete(delta: number): void {
    this.moveMenu("composer", delta);
  }

  /** The pointer is over a row. */
  setAutocompleteActive(index: number): void {
    this.setMenuActive("composer", index);
  }

  /** Replaces the typed token with the row's (`@Ada Lovelace `, `#design `, `😄 `, `/remind `) and puts the caret after it. */
  chooseAutocomplete(index: number): void {
    this.chooseIn("composer", index);
  }

  /** Escape or a click outside: the menu closes until the typed token ends. */
  dismissAutocomplete(): void {
    this.dismissIn("composer");
  }

  /** The editor's menu: a row was chosen (click). */
  chooseEditAutocomplete(index: number): void {
    this.chooseIn("edit", index);
  }

  /** The editor's menu: the pointer is over a row. */
  setEditAutocompleteActive(index: number): void {
    this.setMenuActive("edit", index);
  }

  /** The editor's menu: a click outside closes it until the typed token ends. */
  dismissEditAutocomplete(): void {
    this.dismissIn("edit");
  }

  /** The composer's @ button: types "@" at the caret (after a space if needed) and opens the people menu. */
  openMention(): void {
    if (!this.state.composerAutocomplete) return;
    if (this.state.autocomplete !== undefined && this.state.autocomplete.kind === "mention") {
      this.focusComposer(this.caret);
      return;
    }
    const edit = insertTrigger(this.state.draft, this.caret, "@");
    this.dismissed = undefined;
    this.selectionCollapsed = true;
    this.setDraft(edit.text, edit.caret);
    this.focusComposer(edit.caret);
  }

  private focusComposer(caret: number): void {
    const seq = this.state.composerSelection === undefined ? 1 : this.state.composerSelection.seq + 1;
    this.update({ composerSelection: { start: caret, end: caret, seq } });
  }

  private focusEditor(caret: number): void {
    const e = this.state.editing;
    if (e === undefined) return;
    const seq = e.selection === undefined ? 1 : e.selection.seq + 1;
    this.update({ editing: { ...e, selection: { start: caret, end: caret, seq } } });
  }

  private menuOf(slot: AutocompleteSlot): AutocompleteMenuState | undefined {
    return slot === "composer" ? this.state.autocomplete : this.state.editing?.autocomplete;
  }

  private menuKey(slot: AutocompleteSlot, key: ComposerKey): boolean {
    const m = this.menuOf(slot);
    if (m === undefined) return false;
    if (key === "up" || key === "down") {
      this.moveMenu(slot, key === "down" ? 1 : -1);
      return true;
    }
    if (key === "escape") {
      this.dismissIn(slot);
      return true;
    }
    if (m.items.length > 0) {
      this.chooseIn(slot, m.active);
      return true;
    }
    this.dismissIn(slot);
    return key === "tab";
  }

  private moveMenu(slot: AutocompleteSlot, delta: number): void {
    const m = this.menuOf(slot);
    if (m !== undefined && m.items.length > 0) this.setMenu(slot, { ...m, active: moveActive(m.active, m.items.length, delta) });
  }

  private setMenuActive(slot: AutocompleteSlot, index: number): void {
    const m = this.menuOf(slot);
    if (m !== undefined && index >= 0 && index < m.items.length && index !== m.active) this.setMenu(slot, { ...m, active: index });
  }

  private chooseIn(slot: AutocompleteSlot, index: number): void {
    const m = this.menuOf(slot);
    const cache = this.ws.state.cache;
    const item = m === undefined ? undefined : m.items[index];
    if (m === undefined || item === undefined || cache === undefined) return;
    if (item.kind === "workflow") {
      // A workflow shortcut: the "/…" typed goes away and the workflow runs (Slack).
      if (slot !== "composer") return;
      const draft = `${this.state.draft.slice(0, m.start)}${this.state.draft.slice(m.end)}`.replace(/^\s+$/, "");
      this.dismissed = undefined;
      this.setDraft(draft, m.start);
      this.runWorkflow(item.id);
      return;
    }
    const token = item.kind === "emoji" ? this.rememberEmoji(item.emoji) : completionToken(item, cache);
    if (slot === "composer") {
      const edit = applyCompletion(this.state.draft, m.start, m.end, token);
      this.dismissed = undefined;
      this.selectionCollapsed = true;
      this.setDraft(edit.text, edit.caret);
      this.focusComposer(edit.caret);
      return;
    }
    const e = this.state.editing;
    if (e === undefined) return;
    const edit = applyCompletion(e.draft, m.start, m.end, token);
    this.editDismissed = undefined;
    this.editCollapsed = true;
    this.setEditDraft(edit.text, edit.caret);
    this.focusEditor(edit.caret);
  }

  private dismissIn(slot: AutocompleteSlot): void {
    const m = this.menuOf(slot);
    if (m === undefined) return;
    if (slot === "composer") this.dismissed = { kind: m.kind, start: m.start };
    else this.editDismissed = { kind: m.kind, start: m.start };
    this.setMenu(slot, undefined);
  }

  private setMenu(slot: AutocompleteSlot, menu: AutocompleteMenuState | undefined): void {
    if (menu === this.menuOf(slot)) return;
    if (slot === "edit") {
      const e = this.state.editing;
      if (e === undefined) return;
      if (menu !== undefined) this.update({ editing: { ...e, autocomplete: menu } });
      else {
        const { autocomplete: _closed, ...rest } = e;
        this.update({ editing: rest });
      }
      return;
    }
    if (menu !== undefined) {
      this.update({ autocomplete: menu });
      return;
    }
    const { autocomplete: _closed, ...rest } = this.state;
    this.setState(rest);
  }

  /**
   * The menu for the token at the caret: opens, refreshes (same token: the same menu object) or closes it. A token
   * whose menu Escape closed stays closed until it ends. Returns the menu and the new "dismissed" mark.
   */
  private nextMenu(
    draft: string,
    caret: number,
    collapsed: boolean,
    dismissed: Dismissed | undefined,
    prev: AutocompleteMenuState | undefined,
    commands: boolean,
  ): { menu: AutocompleteMenuState | undefined; dismissed: Dismissed | undefined } {
    const found = collapsed ? findTrigger(draft, Math.min(caret, draft.length)) : undefined;
    const trigger = found !== undefined && (commands || found.kind !== "command") ? found : undefined;
    if (trigger === undefined) return { menu: undefined, dismissed: undefined };
    if (dismissed !== undefined && dismissed.kind === trigger.kind && dismissed.start === trigger.start) return { menu: undefined, dismissed };
    if (prev !== undefined && prev.kind === trigger.kind && prev.start === trigger.start && prev.end === trigger.end && prev.query === trigger.query) {
      return { menu: prev, dismissed: undefined };
    }
    const items = this.autocompleteItems(trigger.kind, trigger.query);
    return { menu: items === undefined ? undefined : autocompleteMenu(trigger, items, prev), dismissed: undefined };
  }

  /** Re-reads the token at the caret: opens, refreshes or closes the menu, and updates the command hint. */
  private refreshAutocomplete(): void {
    if (!this.state.composerAutocomplete) return;
    const draft = this.state.draft;
    const hint = commandHint(draft, this.ws.state.appCommands ?? []);
    const prevHint = this.state.commandHint;
    if (hint === undefined && prevHint !== undefined) {
      const { commandHint: _gone, ...rest } = this.state;
      this.setState(rest);
    } else if (hint !== undefined && (prevHint === undefined || prevHint.label !== hint.label)) this.update({ commandHint: hint });
    const next = this.nextMenu(draft, this.caret, this.selectionCollapsed, this.dismissed, this.state.autocomplete, true);
    this.dismissed = next.dismissed;
    this.setMenu("composer", next.menu);
  }

  /** The editor's menu, like the composer's (no command hint: an edit can't run a command). */
  private refreshEditAutocomplete(): void {
    const e = this.state.editing;
    if (!this.state.composerAutocomplete || e === undefined) return;
    const next = this.nextMenu(e.draft, this.editCaret, this.editCollapsed, this.editDismissed, e.autocomplete, false);
    this.editDismissed = next.dismissed;
    this.setMenu("edit", next.menu);
  }

  private autocompleteItems(kind: AutocompleteKind, query: string): AutocompleteItem[] | undefined {
    const cache = this.ws.state.cache;
    if (cache === undefined) return undefined;
    switch (kind) {
      case "mention":
        return mentionItems(query, cache, this.ws.state.live, { channelId: this.state.channelId, recentAuthorIds: this.recentAuthors(), now: this.ws.deps.clock.now() });
      case "channel":
        return channelItems(query, cache);
      case "command":
        this.ws.loadAppCommands();
        this.ws.loadWorkflows();
        return commandItems(query, this.ws.state.appCommands ?? [], this.state.threadRootId === undefined ? this.channelShortcuts() : []);
      case "emoji":
        if (this.emoji === undefined) {
          this.loadEmoji();
          return undefined;
        }
        return emojiItems(query, this.emoji, parseIds(this.ws.deps.storage.get(RECENT_EMOJI_KEY)));
    }
  }

  /** Authors in this conversation, newest first (the "people here" ranking boost). */
  private recentAuthors(): string[] {
    const out: string[] = [];
    const items = this.state.items;
    for (let i = items.length - 1; i >= 0 && out.length < 20; i--) {
      const it = items[i]!;
      if (it.type === "message" && !out.includes(it.message.authorId)) out.push(it.message.authorId);
    }
    return out;
  }

  private loadEmoji(): void {
    const catalog = this.ws.deps.emoji;
    if (catalog === undefined || this.emojiLoading) return;
    this.emojiLoading = true;
    catalog.load().then(
      (entries) => {
        this.emoji = entries;
        if (this.isDisposed) return;
        this.refreshAutocomplete();
        this.refreshEditAutocomplete();
      },
      () => {
        this.emojiLoading = false;
      },
    );
  }

  /** A ":" autocomplete pick: remembered, counted, and → the emoji to insert (the sink's, in my skin tone). */
  private rememberEmoji(emoji: string): string {
    const recent = parseIds(this.ws.deps.storage.get(RECENT_EMOJI_KEY)).filter((e) => e !== emoji);
    this.ws.deps.storage.set(RECENT_EMOJI_KEY, JSON.stringify([emoji, ...recent].slice(0, RECENT_EMOJI_MAX)));
    // ":" autocomplete picks count toward "Frequently used" like picker picks (Slack).
    if (this.emojiSink !== undefined) return this.emojiSink(emoji) ?? emoji;
    void Promise.resolve().then(() => this.ws.deps.api.recordEmoji(this.ws.wsId, [emoji])).catch(() => undefined);
    return emoji;
  }
}

/** Where the caret lands after an edit we only know the result of: at the end if it was there, else it moves with the text. */
function estimateCaret(before: string, after: string, caret: number): number {
  if (caret >= before.length) return after.length;
  return Math.max(0, Math.min(after.length, caret + after.length - before.length));
}

function parseIds(raw: string | undefined): string[] {
  if (raw === undefined) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** The FileRef shown in a pending message until the server's copy arrives (local preview URL). */
function optimisticFile(a: AttachmentView, u: UploadEntry | undefined): FileRef {
  const f: FileRef = { id: a.id, name: a.name, mime: a.mime, size: a.size, url: a.previewUrl ?? `local-upload:${a.id}` };
  if (u !== undefined && u.width !== undefined) f.width = u.width;
  if (u !== undefined && u.height !== undefined) f.height = u.height;
  return f;
}
